import React from "react";
import { format } from "date-fns";
import styles from "./CommentItem.module.css";
import ProgressStatusBadge from "../ProgressStatusBadge/ProgressStatusBadge";
import { mapStatusToKey } from "../../../Helpers/mapStatusToKey";

export type CommentDisplayCardProps = {
  commenter: string;
  dateMade: Date;
  /** When set, shows the same progress pill as criteria comments */
  progressStatus?: string;
  /** Right side of header (e.g. ⋯ menu or Save/Cancel) */
  trailing?: React.ReactNode;
  children: React.ReactNode;
  /** Merged onto the outer flex row (e.g. KPI nested spacing) */
  className?: string;
  /**
   * When false, only the body `children` are shown (no avatar, name, date, trailing).
   * Used when the parent already shows that metadata (e.g. KPI progress note header).
   */
  showMetadataRow?: boolean;
};

/**
 * Read-only shell shared by {@link CommentItem} and KPI progress-note comments
 * so list styling matches the comments popups.
 */
const CommentDisplayCard: React.FC<CommentDisplayCardProps> = ({
  commenter,
  dateMade,
  progressStatus,
  trailing,
  children,
  className,
  showMetadataRow = true,
}) => {
  const initial = (commenter?.trim()?.charAt(0) || "?").toUpperCase();

  if (!showMetadataRow) {
    return (
      <div
        className={[styles.commentItem, styles.commentItemBodyOnly, className]
          .filter(Boolean)
          .join(" ")}
      >
        <div className={styles.commentContent}>{children}</div>
      </div>
    );
  }

  return (
    <div className={[styles.commentItem, className].filter(Boolean).join(" ")}>
      <div className={styles.commentAvatar}>
        <div className={styles.avatar}>{initial}</div>
      </div>
      <div className={styles.commentContent}>
        <div className={styles.commentHeader}>
          <span className={styles.commenterName}>{commenter || "—"}</span>
          <span className={styles.commentDate}>
            {format(dateMade, "MMMM do, yyyy h:mm a")}
          </span>
          {progressStatus ? (
            <span className={styles.commentProgress}>
              <ProgressStatusBadge
                status={mapStatusToKey(progressStatus)}
                text={progressStatus}
              />
            </span>
          ) : null}
          <div className={styles.commentActions}>{trailing}</div>
        </div>
        {children}
      </div>
    </div>
  );
};

export default CommentDisplayCard;
